import uniqBy from 'lodash/uniqBy';
import type { ITrafficSituation } from '../models/traffic-situation.model';
import type { ILine } from '../models/line.model';
import type { IServiceJourney } from '../models/service-journey.model';
import type { TransportMode } from '../models/transport-mode.enum';
import { DateTime } from 'luxon';
import { TrafficSituationSeverity } from '../models/traffic-situation-severity.enum';
import type { IMunicipality } from '../models/municipality.model';
import { IStopArea } from '../models/stop-area.model';

export class TrafficSituationHelper {
  private static get today(): DateTime {
    return DateTime.now().startOf('day');
  }

  public static toSeveritySortOrder(severity: TrafficSituationSeverity): number {
    switch (severity) {
      case TrafficSituationSeverity.slight:
        return 1;
      case TrafficSituationSeverity.normal:
        return 2;
      case TrafficSituationSeverity.severe:
        return 3;
      default:
        return 0;
    }
  }

  public static getDistinctLines(ts: ITrafficSituation): ILine[] {
    let result: ILine[] = [];

    if (ts.affectedLines) {
      result = result.concat(ts.affectedLines);
    }

    if (ts.affectedJourneys) {
      result = result.concat(ts.affectedJourneys.map((affectedJourney: IServiceJourney) => affectedJourney.line).filter((line) => line !== undefined) as ILine[]);
    }

    return uniqBy(result, (line) => line.gid);
  }

  public static affectsLineWithTransportMode(ts: ITrafficSituation, tm: TransportMode): boolean {
    return TrafficSituationHelper.getDistinctLines(ts).some((l) => l.transportMode === tm);
  }

  public static isCurrentTrafficSituation(trafficSituation: ITrafficSituation): boolean {
    return trafficSituation.startTime.startOf('day') <= TrafficSituationHelper.today && (!trafficSituation.endTime || trafficSituation.endTime.startOf('day') >= TrafficSituationHelper.today);
  }

  public static isNewTrafficSituation(trafficSituation: ITrafficSituation): boolean {
    return TrafficSituationHelper.isCurrentTrafficSituation(trafficSituation) ? trafficSituation.startTime > DateTime.now().minus({ hours: 24 }) : false;
  }

  public static isPlannedTrafficSituation(trafficSituation: ITrafficSituation): boolean {
    return trafficSituation.startTime.startOf('day') > TrafficSituationHelper.today;
  }

  public static getTrafficSituationSeverityEnum(trafficSituationSeverity: string): TrafficSituationSeverity {
    const normalized = trafficSituationSeverity?.toLowerCase?.();
    if (normalized === 'severe') {
      return TrafficSituationSeverity.severe;
    } else if (normalized === 'normal') {
      return TrafficSituationSeverity.normal;
    } else if (normalized === 'slight') {
      return TrafficSituationSeverity.slight;
    }
    return TrafficSituationSeverity.normal;
  }

  public static getAffectedMunicipalities(trafficSituation: ITrafficSituation): IMunicipality[] {
    const distinctLines = TrafficSituationHelper.getDistinctLines(trafficSituation);
    const municipalitiesFromLines = distinctLines.flatMap((line) => line.municipalities ?? []);
    const municipalitiesFromStopPoints = trafficSituation.affectedStopPoints.map((stopPoint) => stopPoint.municipality).filter((m) => m !== undefined);
    return uniqBy([...municipalitiesFromLines, ...municipalitiesFromStopPoints], (m) => m.gid).sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }

  public static getAffectedStopAreas(trafficSituation: ITrafficSituation): IStopArea[] {
    const stopAreasFromStopPoints = trafficSituation.affectedStopPoints.map((sp) => sp.stopArea).filter((sa) => sa !== undefined);
    return uniqBy(stopAreasFromStopPoints, (sa) => sa.gid).sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }
}
